import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Share2, Plus, Trash2, Copy, ExternalLink, Lock, Eye, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useData } from '../context/DataContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SharedPage = () => {
  const { clients, collections, tags, ads, createShareLink, getShareLinks, deleteShareLink } = useData();
  const [shares, setShares] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [newShare, setNewShare] = useState({
    type: 'client',
    client_id: '',
    collection_id: '',
    tag_id: '',
    permission: 'view_only',
    password: '',
    hasPassword: false,
    hasExpiry: false,
    expires_at: ''
  });

  useEffect(() => {
    loadShares();
  }, []);

  const loadShares = async () => {
    try {
      const data = await getShareLinks();
      setShares(data);
    } catch (err) {
      console.error('Failed to load shares:', err);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    setIsLoading(true);
    try {
      const shareData = {
        permission: newShare.permission,
        password: newShare.hasPassword ? newShare.password : null,
        expires_at: newShare.hasExpiry ? newShare.expires_at : null
      };

      if (newShare.type === 'client' && newShare.client_id) {
        shareData.client_id = newShare.client_id;
      } else if (newShare.type === 'collection' && newShare.collection_id) {
        shareData.collection_id = newShare.collection_id;
      } else if (newShare.type === 'tag' && newShare.tag_id) {
        shareData.tag_id = newShare.tag_id;
      }

      await createShareLink(shareData);
      await loadShares();
      setNewShare({
        type: 'client',
        client_id: '',
        collection_id: '',
        tag_id: '',
        permission: 'view_only',
        password: '',
        hasPassword: false,
        hasExpiry: false,
        expires_at: ''
      });
      setIsDialogOpen(false);
      toast.success('Share link created');
    } catch (err) {
      toast.error('Failed to create share link');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (shareId) => {
    if (window.confirm('Delete this share link?')) {
      try {
        await deleteShareLink(shareId);
        setShares(shares.filter(s => s.id !== shareId));
        toast.success('Share link deleted');
      } catch (err) {
        toast.error('Failed to delete');
      }
    }
  };

  const copyLink = (shareCode) => {
    const link = `${window.location.origin}/share/${shareCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  const getShareName = (share) => {
    if (share.client_id) {
      const client = clients.find(c => c.id === share.client_id);
      return client ? `Client: ${client.name}` : 'Client share';
    }
    if (share.collection_id) {
      const collection = collections.find(c => c.id === share.collection_id);
      return collection ? `Collection: ${collection.name}` : 'Collection share';
    }
    if (share.tag_id) {
      const tag = tags.find(t => t.id === share.tag_id);
      return tag ? `Tag: #${tag.name}` : 'Tag share';
    }
    return 'Selected ads';
  };

  return (
    <div className="min-h-screen" data-testid="shared-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Share2 className="w-7 h-7 text-violet-400" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">Shared Links</h1>
          </div>
          <p className="text-zinc-500">Share ads with clients and team members</p>
        </div>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-500" data-testid="create-share-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Link
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white">Create Share Link</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-400">Share Type</Label>
                <Select
                  value={newShare.type}
                  onValueChange={(value) => setNewShare({ ...newShare, type: value })}
                >
                  <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="client" className="text-zinc-200">By Client</SelectItem>
                    <SelectItem value="collection" className="text-zinc-200">By Collection</SelectItem>
                    <SelectItem value="tag" className="text-zinc-200">By Tag</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newShare.type === 'client' && (
                <div>
                  <Label className="text-zinc-400">Client</Label>
                  <Select
                    value={newShare.client_id}
                    onValueChange={(value) => setNewShare({ ...newShare, client_id: value })}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                      <SelectValue placeholder="Select client..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {clients.map((client) => (
                        <SelectItem key={client.id} value={client.id} className="text-zinc-200">
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {newShare.type === 'collection' && (
                <div>
                  <Label className="text-zinc-400">Collection</Label>
                  <Select
                    value={newShare.collection_id}
                    onValueChange={(value) => setNewShare({ ...newShare, collection_id: value })}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                      <SelectValue placeholder="Select collection..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {collections.map((coll) => (
                        <SelectItem key={coll.id} value={coll.id} className="text-zinc-200">
                          {coll.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {newShare.type === 'tag' && (
                <div>
                  <Label className="text-zinc-400">Tag</Label>
                  <Select
                    value={newShare.tag_id}
                    onValueChange={(value) => setNewShare({ ...newShare, tag_id: value })}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                      <SelectValue placeholder="Select tag..." />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {tags.map((tag) => (
                        <SelectItem key={tag.id} value={tag.id} className="text-zinc-200">
                          #{tag.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-zinc-400">Permission</Label>
                <Select
                  value={newShare.permission}
                  onValueChange={(value) => setNewShare({ ...newShare, permission: value })}
                >
                  <SelectTrigger className="mt-1.5 bg-zinc-800 border-zinc-700 text-zinc-200">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="view_only" className="text-zinc-200">View only</SelectItem>
                    <SelectItem value="comments" className="text-zinc-200">Allow comments</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-zinc-500" />
                  <Label className="text-zinc-400">Password protect</Label>
                </div>
                <Switch
                  checked={newShare.hasPassword}
                  onCheckedChange={(checked) => setNewShare({ ...newShare, hasPassword: checked })}
                />
              </div>

              {newShare.hasPassword && (
                <Input
                  type="password"
                  placeholder="Enter password"
                  value={newShare.password}
                  onChange={(e) => setNewShare({ ...newShare, password: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-zinc-500" />
                  <Label className="text-zinc-400">Set expiry</Label>
                </div>
                <Switch
                  checked={newShare.hasExpiry}
                  onCheckedChange={(checked) => setNewShare({ ...newShare, hasExpiry: checked })}
                />
              </div>

              {newShare.hasExpiry && (
                <Input
                  type="datetime-local"
                  value={newShare.expires_at}
                  onChange={(e) => setNewShare({ ...newShare, expires_at: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              )}

              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-violet-600 hover:bg-violet-500"
              >
                {isLoading ? 'Creating...' : 'Create Share Link'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Shares List */}
      {shares.length === 0 ? (
        <div className="text-center py-20">
          <div className="w-20 h-20 rounded-full bg-zinc-800/50 flex items-center justify-center mx-auto mb-4">
            <Share2 className="w-10 h-10 text-zinc-600" />
          </div>
          <h3 className="text-xl font-semibold text-zinc-300 mb-2">No shared links</h3>
          <p className="text-zinc-500 mb-6">Create share links to share ads with others</p>
          <Button
            onClick={() => setIsDialogOpen(true)}
            className="bg-violet-600 hover:bg-violet-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Link
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {shares.map((share, index) => (
            <motion.div
              key={share.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-zinc-900/50 border border-white/5 rounded-xl p-5"
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-zinc-200">{getShareName(share)}</h3>
                  <div className="flex items-center gap-3 mt-2 text-sm text-zinc-500">
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {share.view_count} views
                    </span>
                    {share.has_password && (
                      <Badge variant="outline" className="border-zinc-700 text-zinc-400">
                        <Lock className="w-3 h-3 mr-1" />
                        Protected
                      </Badge>
                    )}
                    {share.expires_at && (
                      <span>
                        Expires: {new Date(share.expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyLink(share.share_code)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => window.open(`/share/${share.share_code}`, '_blank')}
                    className="text-zinc-400 hover:text-white"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(share.id)}
                    className="text-zinc-400 hover:text-red-400"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SharedPage;
